/**
 * Saber — GEN-EKB-018. Zona del raíl: cabecera con Jimmy, el rol de quien mira y las cifras de
 * lo que sabe la empresa (calculadas por el kernel solo sobre los KBs permitidos: GET /v1/ekb/saber).
 * Mockup: Saber-1-mapa.html (.hero, .kpis, .sug). El cuerpo «Lo que sabemos» (GEN-EKB-019) es la rejilla de áreas,
 * la lista de iKus de un KB y el detalle de un iKu. Las sugerencias (GEN-EKB-022) abren el chat con Jimmy con la frase escrita, sin enviarla.
 * GEN-EKB-024: «Pendiente de ti» (SaberPendientes) bajo la cabecera.
 */
import { useEffect, useRef, useState } from 'react'
import { getAreas, getIku, listIkus, listKbs, registrarUsoArea } from '../api/client.js'
import { claveDe, pedirSaber, saberGuardado } from '../lib/saberCache.js'
import { useSession } from '../context/SessionContext.jsx'
import { useFotoAgente } from '../context/FotosAgentes.jsx'
import { Avatar } from '../despacho/ui.jsx'
import tenantConfig from '../tenant/tenant.config.js'
import SaberMapa, { SaberArea, plural } from './SaberMapa.jsx'
import SaberPendientes from './SaberPendientes.jsx'
import SaberEncargos from './SaberEncargos.jsx'
import { DetalleIku, ListaIkus } from './SaberKb.jsx'
import { Modal, ModalKb, ModalSaber } from './SaberAltas.jsx'
import { useGestionAreas } from './AreasPanel.jsx'
import { areaDeHash, PREFIJO_AREA } from '../lib/citaIku.js'
import PermisosPanel from './PermisosPanel.jsx'

const ROLES = { admin: 'Administrador', champion: 'Champion', usuario: 'Usuario' }
const fmt = n => Number(n ?? 0).toLocaleString('es-ES', { useGrouping: 'always' })
const SUGERENCIAS = [
  { icono: '📄', texto: 'Aprende este documento y dime en qué KB va' },
  { icono: '❓', texto: '¿Qué sabemos del convenio del metal?' },
  { icono: '🕳️', texto: '¿Qué nos preguntan y no sabemos?' },
  { icono: '🗂️', texto: 'Propón áreas nuevas para lo que ya hay' },
  { icono: '🌙', texto: '¿Qué aprendiste anoche?' },
]

export default function SaberPanel({ onPedirAJimmy, refrescoSaber = 0, ikuPedido = null }) {
  const { agents, user } = useSession()
  const fotoDe = useFotoAgente()
  // GEN-PX-033: lo precargado al entrar se pinta al momento; la lectura de abajo lo revalida y repinta si cambió.
  const clave = claveDe(user)
  const getSaber = () => pedirSaber(clave)
  const [resumen, setResumen] = useState(() => saberGuardado(clave))
  const [error, setError] = useState(false)
  const [vista, setVista] = useState({ kb: null, ikuId: null }) // mapa → KB → iKu
  const [gestion, setGestion] = useState(new Map()) // GEN-PX-019: id de KB → KB de listKbs (con `puede_gestionar`)
  const [verRetirados, setVerRetirados] = useState(false)
  const [nota, setNota] = useState(null)
  const [modal, setModal] = useState(null) // null | 'kb' | 'saber' (GEN-EKB-020)
  const [areaCtx, setAreaCtx] = useState(null) // GEN-PX-020e: área desde cuya tarjeta se abrió «+ KB» o «+ Añadir saber»; null = cabecera, sin filtro
  const [areasAdm, setAreasAdm] = useState([]) // GEN-PX-020: áreas con su padre y `sistema` (GET /v1/ekb/areas), solo administrador
  const [verPermisos, setVerPermisos] = useState(false) // false | true (todos) | { nombre, kbIds } (GEN-PX-020e: solo un área y sus subáreas). GEN-PX-020: mapa de permisos de KBs (GEN-EKB-013)

  // GEN-PX-031: la página de un área vive en el hash (#/saber/area/<id>): atrás funciona y se puede compartir.
  const [areaId, setAreaId] = useState(() => areaDeHash(window.location.hash))
  useEffect(() => {
    const leer = () => setAreaId(areaDeHash(window.location.hash))
    window.addEventListener('hashchange', leer)
    return () => window.removeEventListener('hashchange', leer)
  }, [])
  // GEN-PX-032: abrir un área cuenta como uso suyo; al volver al mapa se pide de nuevo, ya con el orden actualizado.
  const uso = useRef(Promise.resolve())
  const previa = useRef(null)
  useEffect(() => {
    let vivo = true
    if (areaId) uso.current = (async () => { try { await registrarUsoArea(areaId) } catch { /* el orden no es crítico */ } })()
    else if (previa.current) uso.current.then(() => getSaber()).then(r => vivo && r && setResumen(r)).catch(() => {})
    previa.current = areaId
    return () => { vivo = false }
  }, [areaId])
  const verArea = id => { window.location.hash = `${PREFIJO_AREA}${id}` }
  const volverDelArea = () => {
    window.history.pushState(null, '', window.location.pathname + window.location.search)
    setAreaId(null)
  }

  useEffect(() => {
    let vivo = true
    getSaber().then(r => vivo && setResumen(prev => (JSON.stringify(prev) === JSON.stringify(r) ? prev : r))).catch(() => vivo && setError(true))
    return () => { vivo = false }
  }, [refrescoSaber]) // GEN-EKB-022b: cada respuesta completa de Jimmy sube el contador y se vuelve a pedir el resumen

  // GEN-PX-019: quién puede gestionar cada KB lo dice el kernel (`puede_gestionar` de GET /v1/ekb/kbs); sin perfil EKB
  // (usuario) la llamada falla y no hay acciones. Incluye los retirados para «Ver retirados».
  async function cargarGestion() {
    try {
      const r = await listKbs('todos')
      setGestion(new Map((r.kbs ?? []).map(k => [k.id, k])))
    } catch {
      setGestion(new Map())
    }
  }
  useEffect(() => { cargarGestion() }, [refrescoSaber])

  // Tras editar o retirar un KB. La edición se pinta al momento con lo que el kernel acaba de guardar (PUT 200) en la
  // cabecera, el enlace «← KB», el mapa y los permisos, y se vuelve a pedir el resumen sin bloquear lo pintado: si esa
  // lectura falla o llega con datos viejos (caché), el cambio no se pierde (GEN-PX-019: antes solo se actualizaba
  // `vista.kb.nombre` tras dos esperas, y ni el mapa ni la lista de permisos se tocaban hasta recargar la página).
  async function cambioKb({ kb, retirado, pendiente, nombre }) {
    const parche = k => (kb && k.id === kb.id ? { ...k, nombre: kb.nombre, descripcion: kb.descripcion } : k)
    const aplicar = r => (kb && r ? { ...r, mapa: (r.mapa ?? []).map(a => ({ ...a, kbs: a.kbs.map(parche) })) } : r)
    if (kb) {
      setResumen(aplicar)
      setGestion(g => (g.has(kb.id) ? new Map(g).set(kb.id, parche(g.get(kb.id))) : g))
      setVista(v => (v.kb?.id === kb.id ? { ...v, kb: parche(v.kb) } : v))
    }
    try { setResumen(aplicar(await getSaber())) } catch { /* se queda lo pintado */ }
    try {
      const r = await listKbs('todos')
      setGestion(new Map((r.kbs ?? []).map(k => [k.id, parche(k)])))
    } catch { /* idem */ }
    if (retirado) {
      setVista({ kb: null, ikuId: null })
      setNota(pendiente ? `La retirada de «${nombre}» rebaja la etiqueta efectiva de ${pendiente} iKu(s): queda pendiente hasta que la confirme su titular.` : `KB «${nombre}» retirado.`)
    }
  }
  const esAdmin = resumen?.rol === 'admin'
  const cargarAreas = async () => { try { setAreasAdm((await getAreas()).areas ?? []) } catch { setAreasAdm([]) } }
  useEffect(() => { if (esAdmin) cargarAreas() }, [esAdmin, refrescoSaber])
  // Tras renombrar, mover o retirar un área (o mover un KB) se vuelve a pedir el mapa y las áreas: sin recargar la página.
  const ultima = useRef(null) // GEN-PX-020f: la acción de gestión en curso, para volver al mapa si lo que se retira es el área abierta
  const areas = useGestionAreas({
    areas: areasAdm,
    onHecho: async () => {
      await cargarAreas()
      try { setResumen(await getSaber()) } catch { /* se queda lo pintado */ }
      if (ultima.current?.tipo === 'retirar' && ultima.current.areaId === areaId) volverDelArea()
    },
  })
  const abrirGestion = (tipo, extra) => { ultima.current = { tipo, areaId: extra?.area?.id }; areas.abrir(tipo, extra) }
  // GEN-PX-020e: KBs del área y de todas sus subáreas. Jerarquía: `padre_id` del mapa y de getAreas; KBs: los del mapa y los de getAreas.
  const subarbolDe = a => {
    const mapa = resumen?.mapa ?? []
    const padre = new Map([...areasAdm.map(x => [x.id, x.padre_id]), ...mapa.map(x => [x.id, x.padre_id])])
    const ids = new Set([a.id])
    for (let crecio = true; crecio;) {
      crecio = false
      for (const [id, p] of padre) if (p && ids.has(p) && !ids.has(id)) { ids.add(id); crecio = true }
    }
    const kbIds = new Set()
    for (const x of mapa) if (ids.has(x.id)) x.kbs.forEach(k => kbIds.add(k.id))
    for (const x of areasAdm) if (ids.has(x.id)) (x.kbs ?? []).forEach(k => kbIds.add(k.id))
    return kbIds
  }
  const permisosDeArea = a => setVerPermisos({ nombre: a.nombre, kbIds: [...subarbolDe(a)] })
  const gestionAreas = esAdmin ? {
    abrir: abrirGestion,
    permisos: permisosDeArea,
    sistema: new Set(areasAdm.filter(a => a.sistema).map(a => a.id)),
    padres: new Map(areasAdm.map(a => [a.id, a.padre_id])),
  } : null
  const retirados = [...gestion.values()].filter(k => k.estado === 'retirado' && k.puede_gestionar)

  // GEN-EKB-023: una cita de Jimmy pide abrir un iKu. Se abre en el centro, dentro de uno de sus KBs que la persona
  // puede ver (el kernel solo devuelve esos); si el kernel no lo deja abrir, el detalle dice «No disponible».
  useEffect(() => {
    if (!ikuPedido?.kbId) return // GEN-EKB-024: enlace a un KB (el que da Jimmy al crearlo): su lista de iKus
    let vivo = true
    listIkus(ikuPedido.kbId, 'todos')
      .then(r => vivo && setVista({ kb: r?.kb ?? { id: ikuPedido.kbId, nombre: 'Lo que sabemos' }, ikuId: null }))
      .catch(() => vivo && setVista({ kb: { id: ikuPedido.kbId, nombre: 'Lo que sabemos' }, ikuId: null }))
    return () => { vivo = false }
  }, [ikuPedido])

  useEffect(() => {
    if (!ikuPedido?.id) return
    let vivo = true
    getIku(ikuPedido.id)
      .then(r => vivo && setVista({ kb: r?.iku?.contenedores?.[0] ?? { id: null, nombre: 'Lo que sabemos' }, ikuId: ikuPedido.id }))
      .catch(() => vivo && setVista({ kb: { id: null, nombre: 'Lo que sabemos' }, ikuId: ikuPedido.id }))
    return () => { vivo = false }
  }, [ikuPedido])

  // KBs del mapa con el área a la vista, para el selector de destino de «+ Añadir saber».
  // GEN-EKB-044: un KB que se tiene en cuenta en otra área (`tambien_en`) sale en el mapa varias veces; aquí, una vez, en su área principal.
  const kbsDelMapa = (resumen?.mapa ?? []).flatMap(a => a.kbs.filter(k => !k.tambien_en).map(k => ({ ...k, area: a.nombre })))
  const acciones = {
    area: esAdmin ? () => areas.abrir('nueva') : null,
    permisos: esAdmin ? () => setVerPermisos(true) : null,
    kb: resumen?.rol === 'admin' ? () => { setAreaCtx(null); setModal('kb') } : null,
    saber: kbsDelMapa.some(k => k.puede_editar) ? () => { setAreaCtx(null); setModal('saber') } : null,
  }
  // GEN-PX-020e: las mismas altas desde la tarjeta de un área; «+ Añadir saber» solo si el área (con sus subáreas) tiene un KB donde escribir.
  const kbsEditablesDe = a => { const ids = subarbolDe(a); return kbsDelMapa.filter(k => ids.has(k.id) && k.puede_editar) }
  const accionesMapa = {
    ...acciones,
    kbEn: acciones.kb ? a => { setAreaCtx({ id: a.id }); setModal('kb') } : null,
    saberEn: acciones.saber ? a => { setAreaCtx({ id: a.id, nombre: a.nombre }); setModal('saber') } : null,
    saberEnDisponible: a => kbsEditablesDe(a).length > 0,
  }
  // Tras un alta se vuelve a pedir el resumen: cifras y rejilla salen del kernel, no se calculan aquí.
  async function refrescar() {
    setResumen(await getSaber())
    setModal(null)
  }

  // La ficha de Jimmy da foto y cargo; si no está en el Pórtico, iniciales y sin cargo.
  const jimmy = agents.find(a => a.nombre === 'Jimmy')
  const rol = ROLES[resumen?.rol ?? user?.rol] ?? (resumen?.rol ?? user?.rol ?? '')

  return (
    <div className="dk-saber">
      <header className="dk-saber__hero">
        <Avatar foto={jimmy ? fotoDe(jimmy) : null} iniciales="Ji" nombre="Jimmy" tamano={84} activo agente />
        <div className="dk-saber__texto">
          <div className="dk-saber__eyebrow">Saber · lo que sabe tu empresa</div>
          <h1 className="dk-saber__h1">Jimmy gestiona el saber de {tenantConfig.nombre}.</h1>
          {jimmy?.rol && <div className="dk-saber__cargo">{jimmy.rol}</div>}
          <p>Pídele que la empresa aprenda un documento, una web o una conversación, o pregúntale qué sabemos y qué nos falta. Él redacta con el Escriba, lo coloca en el KB que toca y te deja la traza.</p>
          <div className="dk-saber__perm"><i aria-hidden="true" /> Jimmy actúa con tus permisos de <b>{rol}</b> · ve y toca solo los KBs que tú puedes ver</div>
        </div>
        {resumen && (
          <div className="dk-saber__kpis">
            <div className="dk-saber__kpi"><b>{fmt(resumen.kbs)}</b><span>{resumen.kbs === 1 ? 'KB' : 'KBs'} en {plural(resumen.areas, 'área', 'áreas')}</span></div>
            <div className="dk-saber__kpi"><b>{fmt(resumen.ikus)}</b><span>{resumen.ikus === 1 ? 'iKu vigente' : 'iKus vigentes'}</span></div>
          </div>
        )}
      </header>
      {!resumen && !error && <p role="status" className="dk-saber__aviso">Cargando las cifras y el mapa de Saber…</p>}
      {error && !resumen && <p role="alert" className="dk-saber__aviso">No se han podido cargar las cifras de Saber.</p>}
      {/* GEN-EKB-024: lo que Jimmy preparó y espera mi confirmación; solo aparece si hay alguno. */}
      <SaberPendientes refresco={refrescoSaber} onResuelto={refrescar} />
      {nota && <p className="dk-saber__aviso" role="status">{nota}</p>}
      {verPermisos && esAdmin && <PermisosPanel kbIds={verPermisos.kbIds} areaNombre={verPermisos.nombre} onVolver={() => { setVerPermisos(false); cargarGestion() }} />}
      {resumen && !vista.kb && !verPermisos && areas.accion && (
        <Modal titulo={areas.titulo} rotulo={areas.rotulo} onCerrar={areas.cerrar}>{areas.formularioModal}</Modal>
      )}
      {resumen && !vista.kb && !verPermisos && !areaId && <SaberMapa mapa={resumen.mapa ?? []} resumen={resumen} acciones={accionesMapa} onVerArea={verArea} onAbrirKb={kb => setVista({ kb, ikuId: null })} />}
      {resumen && !vista.kb && !verPermisos && areaId && (
        <SaberArea area={(resumen.mapa ?? []).find(a => a.id === areaId)} mapa={resumen.mapa ?? []} onVerArea={verArea} gestion={gestionAreas} acciones={accionesMapa} onVolver={volverDelArea} onAbrirKb={kb => setVista({ kb, ikuId: null })} />
      )}
      {resumen && !vista.kb && !verPermisos && !areaId && retirados.length > 0 && (
        <section className="dk-mapa" aria-label="KBs retirados">
          <button type="button" className="dk-btn" onClick={() => setVerRetirados(v => !v)}>{verRetirados ? 'Ocultar retirados' : 'Ver retirados'}</button>
          {verRetirados && (
            <ul className="dk-area">
              {retirados.map(k => <li key={k.id}>{k.nombre} · retirado{k.retirado_en ? ` el ${new Date(k.retirado_en).toLocaleDateString('es-ES')}` : ''}</li>)}
            </ul>
          )}
        </section>
      )}
      {/* GEN-PX-021: el mapa de áreas («Lo que sabemos») y sus atajos van antes que «Lo último de Jimmy». */}
      <div className="dk-saber__sug">
        <b>Pídele a Jimmy:</b>
        {SUGERENCIAS.map(s => (
          <button type="button" key={s.texto} onClick={() => onPedirAJimmy?.(s.texto)}>{s.icono} {s.texto}</button>
        ))}
      </div>
      {/* GEN-EKB-029 / GEN-PX-033: «Lo último de Jimmy» — los 3 encargos de aprendizaje más recientes; «Ver» abre el KB. */}
      <SaberEncargos refresco={refrescoSaber} onVer={kb => setVista({ kb, ikuId: null })} />
      {vista.kb && !vista.ikuId && (
        <ListaIkus key={refrescoSaber} kb={vista.kb} gestion={gestion.get(vista.kb.id)} onCambioKb={cambioKb} onVolver={() => setVista({ kb: null, ikuId: null })} onAbrirIku={ikuId => setVista(v => ({ ...v, ikuId }))} />
      )}
      {modal === 'kb' && <ModalKb areaInicial={areaCtx?.id ?? null} onCerrar={() => setModal(null)} onCreado={refrescar} />}
      {modal === 'saber' && (
        <ModalSaber
          kbs={areaCtx ? kbsEditablesDe(areaCtx) : kbsDelMapa}
          onCerrar={() => setModal(null)}
          onCreado={async (iku, kb) => { await refrescar(); setVista({ kb, ikuId: null }) }}
        />
      )}
      {vista.kb && vista.ikuId && <DetalleIku kb={vista.kb} ikuId={vista.ikuId} puedeGestionar={Boolean(gestion.get(vista.kb.id)?.puede_gestionar)} onCambio={() => getSaber().then(setResumen).catch(() => {})} onVolver={() => setVista(v => (v.kb?.id ? { ...v, ikuId: null } : { kb: null, ikuId: null }))} />}
    </div>
  )
}
